// src/components/features/timetables/Timetable/Timetable.tsx
import type { ComponentProps } from 'preact';
import type { LineAssets, TimetableNotice } from '../../../../arcticapi/types';
import { TimetableNotes } from '../Notes/TimetableNotes';
import { TimetableAssets } from '../Assets/TimetableAssets';
import { TimetableControls } from '../Controls/TimetableControls';
import {
  TimetableGrid,
  type TimetableGridJourney,
  type TimetableGridRow,
} from '../Grid/TimetableGrid';
import { CardBackdrop } from '../../../base/CardBackdrop/CardBackdrop';
import { TimetableLegend } from '../Legend/TimetableLegend';
import styles from './Timetable.module.css';

export interface TimetableProps extends Omit<
  ComponentProps<'section'>,
  'children'
> {
  waypoints: TimetableGridRow[];
  journeys: TimetableGridJourney[];
  notices?: TimetableNotice[];
  assets?: LineAssets | null;
  direction: 'outbound' | 'inbound';
  directionLabels?: Record<'outbound' | 'inbound', string>;
  stopDisplay: 'major' | 'all';
  controlsStopDisplay?: 'major' | 'all';
  date: string;
  loading?: boolean;
  onDirectionChange: (value: 'outbound' | 'inbound') => void;
  onStopDisplayChange: (value: 'major' | 'all') => void;
  onDateChange: (value: string) => void;
  onSubmit: (event: SubmitEvent) => void;
  onShowStopDeparturesDetails?: (waypoint: TimetableGridRow) => void;
}

/**
 * Timetable view container.
 *
 * Includes the timetable controls and timetable grid.
 */
export function Timetable({
  waypoints,
  journeys,
  notices = [],
  assets = null,
  direction,
  directionLabels,
  stopDisplay,
  controlsStopDisplay,
  date,
  loading = false,
  onDirectionChange,
  onStopDisplayChange,
  onDateChange,
  onSubmit,
  onShowStopDeparturesDetails,
  className = '',
  ...sectionProps
}: TimetableProps) {
  const selectedStopDisplay = controlsStopDisplay ?? stopDisplay;
  const visibleWaypointIndexes = waypoints
    .map((waypoint, index) => ({ waypoint, index }))
    .filter(({ waypoint }) => stopDisplay === 'all' || waypoint.major)
    .map(({ index }) => index);

  const visibleWaypoints = visibleWaypointIndexes.map(
    (index) => waypoints[index],
  );
  const visibleJourneys = journeys.map((journey) => ({
    ...journey,
    waypointCells: visibleWaypointIndexes.map(
      (waypointIndex) => journey.waypointCells[waypointIndex],
    ),
  }));

  return (
    <section className={`${styles.root} ${className}`.trim()} {...sectionProps}>
      <div className={styles.content}>
        <CardBackdrop className={styles.controlsCard}>
          <TimetableControls
            direction={direction}
            directionLabels={directionLabels}
            stopDisplay={selectedStopDisplay}
            date={date}
            loading={loading}
            onDirectionChange={onDirectionChange}
            onStopDisplayChange={onStopDisplayChange}
            onDateChange={onDateChange}
            onSubmit={onSubmit}
          />
          {/* Note that we intentionally have a non-card component on the card backdrop */}
          <TimetableLegend></TimetableLegend>
        </CardBackdrop>
        <div className={styles.gridScroll}>
          <TimetableGrid
            waypoints={visibleWaypoints}
            journeys={visibleJourneys}
            notices={notices}
            onShowStopDeparturesDetails={onShowStopDeparturesDetails}
          />
        </div>
        <TimetableNotes notices={notices} />
        {assets && <TimetableAssets assets={assets} />}
      </div>
    </section>
  );
}
